import React from 'react'
import { observer } from 'mobx-react-lite'
import { endOfMonth } from 'date-fns/endOfMonth'
import { getUnixTime } from 'date-fns/getUnixTime'
import { startOfMonth } from 'date-fns/startOfMonth'
import { startOfToday } from 'date-fns/startOfToday'

import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import { useTheme } from '@mui/material/styles'

import type { DateRange } from '@mui/x-date-pickers-pro'

import {
  ANALYTICS_EVENTS,
  Drawer,
  Loader,
  SearchBox,
  useAccountStore,
  useAppConfig,
  useEvent,
  useGlobalCopyStore,
  useGlobalUiStore,
  useScreenSize,
  useTransactionStore,
  useUserStore,
  useWidgetLoadTimer,
  WidgetContainer,
} from '../common'
import { WidgetProps } from '../core'

import { useTransactionsUiStore } from './context/hooks'
import { DEFAULT_TRANSACTION } from '../core/constants/Transaction'
import { exportTransactionsToCSV } from '../core/utils/TransactionUtils'
import ConnectDrawer from '../common/components/ConnectDrawer'
import ManualTransactionDetails from './components/shared/transactiondetails/ManualTransactionDetails'
import TransactionDetails from './components/shared/TransactionDetails'
import TransactionList from './components/shared/TransactionList'
import TransactionTable from './components/shared/TransactionTable'
import AddAccountModal from './components/transactionwidget/AddAccountModal'
import EditCategoryAction from './components/transactionwidget/actions/EditCategoryAction'

import { useCategoryUiStore } from '../categories'
import { useAriaLive } from '../common/hooks/useAriaLive'
import { SkeletonLoader } from '../common/components/SkeletonLoader'
import { buildDynamicCopy } from '../core/utils/Localization'

const HEADER_HEIGHT = 120

enum Drawers {
  AddTransaction = 'addTransaction',
  Category = 'category',
  Transaction = 'transaction',
}

interface TransactionWidgetProps extends WidgetProps {
  height?: string | number
  showInsights?: boolean
}

const TransactionWidget: React.FC<TransactionWidgetProps> = ({
  onBackClick,
  onMenuClick,
  height = '100dvh',
  showInsights = false,
  sx = {},
}) => {
  const { manualAccounts, refreshAccounts } = useAccountStore()
  const { is_mobile_webview, clientConfig, config } = useAppConfig()
  const { setSearchValue: setCategorySearchValue } = useCategoryUiStore()
  const { onEvent } = useEvent()
  const { common: commonCopy, transactions: copy } = useGlobalCopyStore()
  const { displayedDateRange, isInitialized, selectedAccountGuids, setDisplayedDate } =
    useGlobalUiStore()
  const { isDesktop, isTablet } = useScreenSize()
  const theme = useTheme()
  const {
    addTransaction,
    filter,
    isTransactionDataLoaded,
    loadTransactionData,
    setFilter,
    sortedTransactions,
    sortedTransactionsWithSplits,
    tags,
    detailedTransactions: transactions,
    updateTransaction,
  } = useTransactionStore()
  const {
    clearExpandedSplits,
    isAmountValid,
    manualTransaction,
    searchValue,
    setSearchValue,
    setShowInsights,
    updateManualTransaction,
  } = useTransactionsUiStore()
  const { userProfile } = useUserStore()

  const [drawerOpen, setDrawerOpen] = React.useState('')
  const [transactionGuid, setTransactionGuid] = React.useState('')

  const [addManualAccountModalOpen, setAddManualAccountModalOpen] = React.useState(false)
  const [showConnectWidget, setShowConnectWidget] = React.useState(false)

  const { announce, ariaLive } = useAriaLive()

  const selected_account_guid = clientConfig?.transactions?.selected_account_guid

  useWidgetLoadTimer({
    widgetName: 'TransactionWidget',
    isLoaded: isTransactionDataLoaded,
  })

  React.useEffect(() => {
    if (!isTransactionDataLoaded) {
      loadTransactionData().finally()
    }

    // Set the displayed date to the current month
    const startDate = startOfMonth(startOfToday())
    const endDate = endOfMonth(startOfToday())
    setDisplayedDate(startDate, endDate)

    onEvent(ANALYTICS_EVENTS.TRANSACTIONS_VIEW)
  }, [])

  React.useEffect(() => {
    if (isInitialized) {
      // Update the filter whenever the display date or account filter changes
      setFilter({
        accounts: selected_account_guid ? [selected_account_guid] : selectedAccountGuids,
        dateRange: { start: displayedDateRange.start, end: displayedDateRange.end },
        searchTerm: searchValue,
      })
    }
  }, [isInitialized, displayedDateRange, selectedAccountGuids])

  React.useEffect(() => {
    setShowInsights(showInsights)
  }, [showInsights])

  React.useEffect(() => {
    if (isInitialized) {
      announce(
        buildDynamicCopy(copy.showing_transactions_count, sortedTransactions.length) as string,
      )
    }
  }, [isInitialized, sortedTransactions])

  const transaction = transactionGuid
    ? transactions.find((transaction) => transaction.guid === transactionGuid)
    : undefined

  const isTransactionValid =
    isAmountValid &&
    Boolean(manualTransaction.payee) &&
    Boolean(manualTransaction.account_guid) &&
    Boolean(manualTransaction.category_guid)

  const handleDateChanged = (range: DateRange<Date>) => {
    clearExpandedSplits()
    setDisplayedDate(range[0] as Date, range[1] as Date)
  }

  const handleSearch = (searchTerm: string) => {
    setSearchValue(searchTerm)
    setFilter({
      ...filter,
      searchTerm,
    })
    onEvent(ANALYTICS_EVENTS.TRANSACTIONS_CLICK_SEARCH, { search_term: searchTerm })
  }

  const handleExportCsvClick = () => {
    exportTransactionsToCSV(sortedTransactionsWithSplits, tags)
    onEvent(ANALYTICS_EVENTS.TRANSACTIONS_CLICK_EXPORT)
  }

  const handleAddTransactionClick = () => {
    if (manualAccounts.length > 0) {
      updateManualTransaction({
        ...DEFAULT_TRANSACTION,
        date: getUnixTime(startOfToday()),
        user_guid: userProfile?.user_guid || '',
      })

      setDrawerOpen(Drawers.AddTransaction)
      onEvent(ANALYTICS_EVENTS.TRANSACTIONS_CLICK_ADD)
    } else {
      setAddManualAccountModalOpen(true)
      onEvent(ANALYTICS_EVENTS.TRANSACTIONS_ADD_MANUAL_ACCOUNT_MODAL)
    }
  }

  const handleAddTransaction = async () => {
    if (isTransactionValid) {
      await addTransaction(manualTransaction)
    }

    setDrawerOpen('')
  }

  const handleAddManualAccountClick = () => {
    setAddManualAccountModalOpen(false)
    setShowConnectWidget(true)
  }

  const handleConnectWidgetClose = async () => {
    setShowConnectWidget(false)
    await refreshAccounts()
  }

  const handleTransactionDrawerClose = async () => {
    // mark transaction as viewed
    if (transaction && !transaction.has_been_viewed) {
      await updateTransaction({ ...transaction, has_been_viewed: true })
    }
    setDrawerOpen('')
  }

  if (!isInitialized) {
    return <Loader />
  }

  const actions = []

  if (!is_mobile_webview && (isDesktop || isTablet)) {
    actions.push({
      iconName: 'open_in_new',
      label: copy.export_csv_btn,
      onClick: handleExportCsvClick,
    })
  }

  if (config.show_connections_widget_in_master) {
    actions.push({
      iconName: 'add',
      label: copy.add_transaction_btn,
      onClick: handleAddTransactionClick,
    })
  }

  return (
    <WidgetContainer
      actions={actions}
      calendarActions={{
        onRangeChanged: handleDateChanged,
      }}
      hasDivider={true}
      onAccountsFilterClick={() => onEvent(ANALYTICS_EVENTS.ACCOUNTS_CLICK_FILTER)}
      onBackClick={onBackClick}
      onMenuClick={onMenuClick}
      showAccountFilter={!selected_account_guid}
      sx={sx}
      title={copy.transactions_title}
    >
      {!isTransactionDataLoaded ? (
        <SkeletonLoader sx={{ m: 48 }} variant="list" />
      ) : (
        <Stack>
          {ariaLive}
          <Box>
            <SearchBox
              onChange={handleSearch}
              placeholder={copy.action_bar_search_placeholder}
              searchValue={searchValue}
            />
          </Box>

          <Box
            sx={{
              [theme.breakpoints.down('md')]: { display: 'none' },
              [theme.breakpoints.up('md')]: { display: 'block' },
            }}
          >
            <TransactionTable
              height={`calc(${height} - ${HEADER_HEIGHT}px)`}
              onCategoryClick={(guid: string) => {
                setTransactionGuid(guid)
                setDrawerOpen(Drawers.Category)
              }}
              onRowClick={(guid: string) => {
                setTransactionGuid(guid)
                setDrawerOpen(Drawers.Transaction)
                onEvent(ANALYTICS_EVENTS.TRANSACTIONS_CLICK_DETAILS, { transaction_guid: guid })
              }}
            />
          </Box>
          <Box
            sx={{
              [theme.breakpoints.down('md')]: { display: 'block' },
              [theme.breakpoints.up('md')]: { display: 'none' },
            }}
          >
            <TransactionList
              height={`calc(${height} - ${HEADER_HEIGHT}px)`}
              onClick={(guid: string) => {
                setTransactionGuid(guid)
                setDrawerOpen(Drawers.Transaction)
              }}
              showInsights={showInsights}
            />
          </Box>
        </Stack>
      )}

      <Drawer
        ariaLabelClose={commonCopy.close_aria}
        isOpen={drawerOpen === Drawers.Transaction && Boolean(transaction)}
        onClose={handleTransactionDrawerClose}
        title={copy.details_title}
      >
        {transaction && <TransactionDetails transaction={transaction} />}
      </Drawer>
      {transaction && drawerOpen === Drawers.Category && (
        <EditCategoryAction
          onClose={() => {
            setCategorySearchValue('')
            setDrawerOpen('')
            setTransactionGuid('')
          }}
          transaction={transaction}
        />
      )}
      <Drawer
        ariaLabelClose={commonCopy.close_aria}
        isOpen={drawerOpen === Drawers.AddTransaction}
        isPrimaryDisabled={!isTransactionValid}
        onClose={() => setDrawerOpen('')}
        onPrimaryAction={handleAddTransaction}
        title={copy.add_transaction_title}
      >
        <ManualTransactionDetails />
      </Drawer>
      <AddAccountModal
        copy={{ title: copy.add_manual_account }}
        isOpen={addManualAccountModalOpen}
        onClose={() => {
          setAddManualAccountModalOpen(false)
        }}
        onPrimaryAction={handleAddManualAccountClick}
      />
      <ConnectDrawer
        onClose={handleConnectWidgetClose}
        showConnectWidget={showConnectWidget}
        title={copy.add_manual_account}
      />
    </WidgetContainer>
  )
}

export default observer(TransactionWidget)
